import React from "react";
import styled from "styled-components";
import { ValidationLevel } from "../../rule-engine-core/Common/IDesignValidationProvider";
import { AttentionIcon } from "../../CommonUI/Icon/AttentionIcon";
import { ExpressionReactContext, ExpressionRenderType } from "../Structure/ExpressionReactContext";
import { ExpressionValue } from "../../rule-engine-core/Expressions/ExpressionValue";
import { ExpressionValueType } from "../../rule-engine-core/Expressions/ExpressionValueType";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

interface IValidationError {
    position?: {
        top?: number;
        left?: number;
        bottom?: number;
        right?: number;
    };
    value: ExpressionValue;
    hideErrorMessage?: boolean;
}

export const ValidationError = (props: IValidationError) => {
    const context = React.useContext(ExpressionReactContext);

    let position: {
        top?: number;
        left?: number;
        bottom?: number;
        right?: number;
    } = {};

    let issues = props.value.getValidationIssues();
    if (issues.length === 0) return null;

    if (
        (context.renderType === ExpressionRenderType.COMPACT || context.renderType === ExpressionRenderType.TEXT) &&
        context.hideValidation
    ) {
        return null;
    }

    switch (props.value.valueType) {
        case ExpressionValueType.VALUE:
            position = {
                top: -5,
                right: -5,
            };
            break;
        case ExpressionValueType.SUBEXPRESSION:
            position = {
                top: 0,
                right: context.renderType === ExpressionRenderType.FULL ? -10 : -2,
            };
            break;
        case ExpressionValueType.VARIABLE:
            position = {
                top: 0,
                right: context.renderType === ExpressionRenderType.FULL ? -10 : -2,
            };
            break;
        case ExpressionValueType.UNSET:
            position = {
                top: -5,
                right: -10,
            };
            break;
    }

    return (
        <ValidationErrorRender position={props.position || position}>
            {!props.hideErrorMessage && (
                <>
                    <ErrorMessageRender
                        className="ExpressionValidation-errorMessage"
                        type={issues[0].level}
                        renderType={context.renderType}
                    >
                        {issues[0].issue}
                    </ErrorMessageRender>
                    <ErrorBorderRender
                        className="ExpressionValidation-border"
                        type={issues[0].level}
                        renderType={context.renderType}
                    ></ErrorBorderRender>
                </>
            )}
            <AttentionIcon />
        </ValidationErrorRender>
    );
};

export const ValidationErrorRender = styled.div<{
    position: { top?: number; left?: number; bottom?: number; right?: number };
}>`
    img {
        position: absolute;

        ${(props) =>
            props.position
                ? `
                    ${props.position.top !== undefined ? `top: ${props.position.top}px;` : ``}
                    ${props.position.left !== undefined ? `left: ${props.position.left}px;` : ``}
                    ${props.position.bottom !== undefined ? `bottom: ${props.position.bottom}px;` : ``}
                    ${props.position.right !== undefined ? `right: ${props.position.right}px;` : ``}
                `
                : ``}
        width: 8px;
        height: 18px;
    }

    .ExpressionValidation-errorMessage,
    .ExpressionValidation-border {
        opacity: 0;
        z-index: -1;
    }

    &:hover {
        .ExpressionValidation-errorMessage,
        .ExpressionValidation-border {
            opacity: 1;
            z-index: 1;
        }
    }
`;

const ErrorMessageRender = styled.div<{ type: ValidationLevel; renderType: ExpressionRenderType }>`
    font-size: 8px;
    font-style: normal;
    font-weight: 500;
    line-height: 10px;
    letter-spacing: 0.1px;
    text-align: left;

    white-space: pre;

    z-index: 2;

    position: absolute;
    top: calc(100% + ${(props) => (props.renderType === ExpressionRenderType.FULL ? "6px" : "3px")});
    left: 0;
    color: ${(props) =>
        props.type === ValidationLevel.ERROR ? UIStyleConstants.RED_COLOR : UIStyleConstants.ORANGE_COLOR};
`;

const ErrorBorderRender = styled.div<{ type: ValidationLevel; renderType: ExpressionRenderType }>`
    position: absolute;
    top: ${(props) => (props.renderType === ExpressionRenderType.FULL ? "-5px" : "-2px")};
    left: ${(props) => (props.renderType === ExpressionRenderType.FULL ? "-5px" : "-2px")};
    bottom: ${(props) => (props.renderType === ExpressionRenderType.FULL ? "-5px" : "-2px")};
    right: ${(props) => (props.renderType === ExpressionRenderType.FULL ? "-5px" : "-2px")};

    border: ${(props) => (props.renderType === ExpressionRenderType.FULL ? "2px" : "1px")} solid
        ${(props) =>
            props.type === ValidationLevel.ERROR ? UIStyleConstants.RED_COLOR : UIStyleConstants.ORANGE_COLOR};
    box-sizing: border-box;
    border-radius: ${(props) => (props.renderType === ExpressionRenderType.FULL ? "8px" : "4px")};
`;
